/**
 * React Query hooks for WL-5 (fieldiq-api#1359): invoices from
 * customer-approved field tickets — the Well Servicing hooks (useWsInvoices.ts)
 * carried over, mark-paid through Finance's door included.
 *
 * Same rules as useWirelineSlickline.ts: every key starts ['wirelineSlickline'] and
 * every write invalidates that one prefix (an invoice moves the register, the
 * billable list, the job's invoices and the ticket's invoice line); every read
 * spreads LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a refusal, never as
 * an empty list.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateWl, unwrapWl, unwrapWlList } from './useWirelineSlickline';
import type { WlBillableGroup, WlInvoice, WlInvoiceGenerate, WlInvoiceRecipient, WlInvoiceRow, WlInvoiceVoided } from '../types/wireline-slickline-invoices';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const wlInvoiceKeys = {
  invoices: (p?: Params) => ['wirelineSlickline', 'invoices', clean(p) ?? {}] as const,
  billable: () => ['wirelineSlickline', 'invoicesBillable'] as const,
  invoice: (id: string) => ['wirelineSlickline', 'invoice', id] as const,
  recipient: (id: string) => ['wirelineSlickline', 'invoiceRecipient', id] as const,
};

function useWlInvoiceWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      // Finance → Invoices is where WL invoices list; refresh it with the WL keys.
      qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
      return invalidateWl(qc);
    },
  });
}

export interface WlInvoiceFilters extends Params {
  status?: string;
  customer_id?: string;
  job_id?: string;
}

export function useWlInvoices(filters?: WlInvoiceFilters, enabled = true) {
  return useQuery({
    queryKey: wlInvoiceKeys.invoices(filters),
    queryFn: async () => unwrapWlList<WlInvoiceRow>((await api.wirelineSlickline.invoices.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlBillable(enabled = true) {
  return useQuery({
    queryKey: wlInvoiceKeys.billable(),
    queryFn: async () => unwrapWlList<WlBillableGroup>((await api.wirelineSlickline.invoices.billable()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlInvoice(id: string | undefined) {
  return useQuery({
    queryKey: wlInvoiceKeys.invoice(id ?? ''),
    queryFn: async () => unwrapWl<WlInvoice>((await api.wirelineSlickline.invoices.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Who a send with no `to` would address — the office's write role only (the API gates it as a write). */
export function useWlInvoiceRecipient(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wlInvoiceKeys.recipient(id ?? ''),
    queryFn: async () => unwrapWl<WlInvoiceRecipient>((await api.wirelineSlickline.invoices.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useGenerateWlInvoice = () =>
  useWlInvoiceWrite(async (body: WlInvoiceGenerate) => unwrapWl<WlInvoice>((await api.wirelineSlickline.invoices.generate(body)).data).data);

export const useSendWlInvoice = () =>
  useWlInvoiceWrite(async ({ id, to, cc }: { id: string; to?: string; cc?: string[] }) => {
    const body: { to?: string; cc?: string[] } = {};
    if (to) body.to = to;
    if (cc && cc.length) body.cc = cc;
    return unwrapWl<WlInvoice & { sentToName?: string | null }>((await api.wirelineSlickline.invoices.send(id, body)).data).data;
  });

/**
 * Mark paid goes through Finance's door (PATCH /unified-invoices/:id), so a
 * success moves BOTH lists: every Wireline & Slickline read, and Finance → Invoices'
 * other-verticals list and its band figures. The PATCH answers with the raw
 * row, which nothing here reads; the refetch draws the result.
 */
export function useMarkWlInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.wirelineSlickline.invoices.markPaid(id)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
      qc.invalidateQueries({ queryKey: ['invoice-summary-dashboard'] });
      return invalidateWl(qc);
    },
  });
}

/** Resolves to the voided invoice and the ids of the tickets it released (never dropped on the way). */
export const useVoidWlInvoice = () =>
  useWlInvoiceWrite(async ({ id, reason }: { id: string; reason: string }) => {
    const inv = unwrapWl<WlInvoiceVoided>((await api.wirelineSlickline.invoices.void(id, reason)).data).data;
    return { invoice: inv, releasedTicketIds: Array.isArray(inv?.releasedTicketIds) ? inv!.releasedTicketIds : [] };
  });
